// src/features/users/edit.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  AutocompleteAsyncField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { adjustDataForSubmission } from "@/components/Form/CountryPhone/data";

import {
  categoryOptions,
  roleOptions,
  statusOptions,
  typeOptions,
} from "./filter";

/* ------------------------------------------------------------------ */
/* Schema */
/* ------------------------------------------------------------------ */

const FormSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email"),
  firstname: z.string().nullable().optional(),
  lastname: z.string().nullable().optional(),

  group_user_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.string(),
          name: z.string(),
          text: z.string().nullable().optional(),
        }),
      ])
      .nullable()
      .optional(),
  ),
  group_user_id: z.string().uuid().nullable().optional(),

  tableau_username: z.string().nullable().optional(),
  category: z.coerce.string().nullable().optional(),
  status: z.coerce.string().nullable().optional(),
  role: z.coerce.string().nullable().optional(),
  type: z.coerce.string().nullable().optional(),
});

/* ------------------------------------------------------------------ */
/* Component */
/* ------------------------------------------------------------------ */

export function UsersEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustData: adjustDataForSubmission,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "group_user_name" && fetchedData.group_user_id) {
        return {
          id: fetchedData.group_user_id,
          name: fetchedData.group_user_name || fetchedData.group_user || value,
          text: fetchedData.group_user_name || fetchedData.group_user || value,
        };
      }
      return value;
    },
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                Users {id}
              </CardHeader>

              <Divider />

              <CardBody className="flex flex-col gap-4">
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="email"
                    label={t(`${table_name}.columns.email`, {
                      ns: "tables",
                      defaultValue: "Email",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="firstname"
                    label={t(`${table_name}.columns.firstname`, {
                      ns: "tables",
                      defaultValue: "First Name",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="lastname"
                    label={t(`${table_name}.columns.lastname`, {
                      ns: "tables",
                      defaultValue: "Last Name",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="tableau_username"
                    label={t(`${table_name}.columns.tableau_username`, {
                      ns: "tables",
                      defaultValue: "Tableau Username",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="group_user_name"
                    label={t(`${table_name}.columns.group_user_name`, {
                      ns: "tables",
                      defaultValue: "Group User",
                    })}
                    base_url="admin-quantilia/group_users"
                    column_name="name"
                    id_column="group_user_id"
                    control={form.control}
                  />
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="category"
                    label={t(`${table_name}.columns.category`, {
                      ns: "tables",
                      defaultValue: "Category",
                    })}
                    options={categoryOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="role"
                    label={t(`${table_name}.columns.role`, {
                      ns: "tables",
                      defaultValue: "Role",
                    })}
                    options={roleOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                {/* Row 4 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="status"
                    label={t(`${table_name}.columns.status`, {
                      ns: "tables",
                      defaultValue: "Status",
                    })}
                    options={statusOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="type"
                    label={t(`${table_name}.columns.type`, {
                      ns: "tables",
                      defaultValue: "Type",
                    })}
                    options={typeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["group_user_id", "group_user_name"])
              : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
